import { useHookstate } from "@hookstate/core";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Artist,
  Bundle,
  BundlePurchasePayload,
  Comic,
  Release,
  ServerListResponse,
  ServerResponse,
  ServerSingleResponse,
  User,
} from "../../../types";
import { apiClient } from "../../../services/axiosInstance";
import { ReaderNavigationContext } from "../../../screens/reader/types";
import { buildContextQueryParams } from "../../../utils/buildContextQueryParams";
import { SLIDE_IN_MENUS } from "../../../screens/root/constants";
import { rootComponentsStore } from "../../../hookstate/rootComponentsStore";
import { useIsInReader } from "../../../hooks/useIsInReader";
import {
  buildModalPath,
  isOnDetailLandingPath,
  ModalIdentity,
} from "../detailRoutes";

interface SlideInIdentifierConfig extends ModalIdentity {
  navigationContext?: ReaderNavigationContext;
}

function createMenuInstanceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `menu_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

export function useSlideInMenu() {
  const openMenuState = useHookstate(rootComponentsStore.openSlideInMenu);
  const openMenu = openMenuState.get();
  const modalDepthState = useHookstate(rootComponentsStore.modalDepth);
  const modalDepth = modalDepthState.get();

  const menuConfigStateSubscribed = useHookstate(
    rootComponentsStore.slideInMenuConfig,
  );
  const comicSlug = menuConfigStateSubscribed.comicSlug?.get();
  const releaseUuid = menuConfigStateSubscribed.releaseUuid?.get();
  const menuInstanceId = menuConfigStateSubscribed.menuInstanceId?.get();
  const artistSlug = menuConfigStateSubscribed.artistSlug?.get();
  const username = menuConfigStateSubscribed.username?.get();
  const userSubpage = menuConfigStateSubscribed.userSubpage?.get();
  const bundleUuid = menuConfigStateSubscribed.bundleUuid?.get();
  const navigationContext = menuConfigStateSubscribed.navigationContext?.get({
    noproxy: true,
  });

  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const inReader = useIsInReader();

  const onCloseMenu = useCallback(() => {
    rootComponentsStore.openSlideInMenu.set(undefined);
    rootComponentsStore.slideInMenuConfig.set({});
    rootComponentsStore.modalDepth.set(0);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("modal");
        return next;
      },
      // preventScrollReset: ScrollRestoration scroll-resets on every
      // navigation (including replace) unless this is set. Without it,
      // closing a modal in the reader snaps the vertical reader to page 1.
      { replace: true, preventScrollReset: true },
    );
  }, [setSearchParams]);

  const onToggleSlideInMenu = useCallback(
    (
      menu: SLIDE_IN_MENUS,
      newConfig?: SlideInIdentifierConfig,
      // noHistory parameter retained for caller compatibility (release-info
      // cover carousel passes it). With the URL bridge as the source of
      // truth, browser back/forward IS the history; this flag is now a
      // no-op, but kept to avoid churning callers in this PR.
      _noHistory?: boolean,
    ) => {
      // Reader-specific toggle: tapping the same menu icon while it's open
      // closes the slide-in. URL identity comparison would also produce a
      // no-op, but the user expectation is "close on same-menu tap".
      const currentOpen = openMenuState.get();
      const currentConfig = rootComponentsStore.slideInMenuConfig.get({
        noproxy: true,
      });
      const isSameIdentity =
        currentOpen === menu &&
        currentConfig.comicSlug === newConfig?.comicSlug &&
        currentConfig.releaseUuid === newConfig?.releaseUuid &&
        currentConfig.artistSlug === newConfig?.artistSlug &&
        currentConfig.username === newConfig?.username &&
        currentConfig.userSubpage === newConfig?.userSubpage &&
        currentConfig.bundleUuid === newConfig?.bundleUuid;

      if (currentOpen === menu && inReader && isSameIdentity) {
        onCloseMenu();
        return;
      }

      // Same modal already open with the same identity (outside the reader):
      // no-op so re-clicks don't stack duplicate ?modal= history entries that
      // each need a separate browser back to dismiss.
      if (isSameIdentity) {
        return;
      }

      if (inReader) rootComponentsStore.isSidebarOpen.set(false);

      // Write store + nav context synchronously so useModalRouting sees a
      // matching identity once the URL push lands.
      rootComponentsStore.openSlideInMenu.set(menu);
      rootComponentsStore.slideInMenuConfig.set({
        ...newConfig,
        menuInstanceId: createMenuInstanceId(),
      });

      // Push the URL so refresh/share and browser back/forward all just
      // work. buildModalPath returns null for any identity that's missing
      // a required slug/uuid — those open as modal-only without URL binding.
      const modalPath = buildModalPath(menu, newConfig ?? {});
      if (modalPath) {
        // If we're already on a landing route (e.g., user is browsing
        // /c/batman and clicks an artist link inside ComicDetailsContent),
        // navigate landing-to-landing rather than stacking a modal over a
        // landing.
        //
        // KNOWN ISSUE — currently unreachable because landings are disabled
        // in this version. When landings ship, this branch has a race: the
        // navigate() removes ?modal= from the URL, useModalRouting fires,
        // sees modalParam=null, and clears the store we just wrote. Fix
        // before re-enabling: let useModalRouting open the slide-in from
        // the new pathname instead of writing the store here.
        if (isOnDetailLandingPath(location.pathname)) {
          navigate("/" + modalPath, { preventScrollReset: true });
        } else {
          rootComponentsStore.modalDepth.set(modalDepthState.get() + 1);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set("modal", modalPath);
              return next;
            },
            { preventScrollReset: true },
          );
        }
      }
    },
    [
      openMenuState,
      inReader,
      onCloseMenu,
      modalDepthState,
      setSearchParams,
      navigate,
      location.pathname,
    ],
  );

  const goBack = useCallback(() => {
    rootComponentsStore.modalDepth.set(
      Math.max(0, rootComponentsStore.modalDepth.get() - 1),
    );
    navigate(-1);
  }, [navigate]);

  const openReleaseInfoMenu = useCallback(
    (
      releaseUuid: string,
      comicSlug?: string,
      navigationContext?: ReaderNavigationContext,
    ) => {
      onToggleSlideInMenu(SLIDE_IN_MENUS.releaseInfo, {
        releaseUuid,
        comicSlug,
        navigationContext,
      });
    },
    [onToggleSlideInMenu],
  );

  const openComicInfoMenu = useCallback(
    (comicSlug: string, navigationContext?: ReaderNavigationContext) => {
      onToggleSlideInMenu(SLIDE_IN_MENUS.comicDetails, {
        comicSlug,
        navigationContext,
      });
    },
    [onToggleSlideInMenu],
  );

  const openArtistProfile = useCallback(
    (slug: string, navigationContext?: ReaderNavigationContext) => {
      onToggleSlideInMenu(SLIDE_IN_MENUS.publisher, {
        artistSlug: slug,
        navigationContext,
      });
    },
    [onToggleSlideInMenu],
  );

  const openUserProfile = useCallback(
    (
      username: string,
      navigationContext?: ReaderNavigationContext,
      subpage?: string,
    ) => {
      onToggleSlideInMenu(SLIDE_IN_MENUS.userProfile, {
        username,
        userSubpage: subpage,
        navigationContext,
      });
    },
    [onToggleSlideInMenu],
  );

  const openBundleDetails = useCallback(
    (bundleUuid: string, navigationContext?: ReaderNavigationContext) => {
      onToggleSlideInMenu(SLIDE_IN_MENUS.bundleDetails, {
        bundleUuid,
        navigationContext,
      });
    },
    [onToggleSlideInMenu],
  );

  return useMemo(
    () => ({
      openComicInfoMenu,
      openReleaseInfoMenu,
      openUserProfile,
      openArtistProfile,
      openBundleDetails,
      onToggleSlideInMenu,
      onCloseMenu,
      openMenu,
      comicSlug,
      releaseUuid,
      menuInstanceId,
      artistSlug,
      bundleUuid,
      username,
      userSubpage,
      navigationContext,
      goBack: modalDepth > 0 ? goBack : undefined,
    }),
    [
      openComicInfoMenu,
      openReleaseInfoMenu,
      openUserProfile,
      openArtistProfile,
      openBundleDetails,
      onToggleSlideInMenu,
      onCloseMenu,
      openMenu,
      comicSlug,
      releaseUuid,
      menuInstanceId,
      artistSlug,
      bundleUuid,
      username,
      userSubpage,
      navigationContext,
      modalDepth,
      goBack,
    ],
  );
}

/**
 * Fetch a release by uuid. Backed by `/v1/readV2/{uuid}?releaseDetails=1`.
 * Response includes the bundles tab payload.
 */
export function getRelease(
  releaseUuid: string,
  navigationContext?: ReaderNavigationContext,
) {
  const contextQueryParams = buildContextQueryParams(navigationContext);
  return apiClient.get<ServerResponse<Release>>(
    `/v1/readV2/${releaseUuid}?releaseDetails=1${contextQueryParams.size > 0 ? "&" + contextQueryParams : ""}`,
  );
}

export function getComicReleases(comicSlug: string, langId?: string) {
  return apiClient.get<ServerListResponse<Release>>(
    `/v1/comics/${comicSlug}/releases?lang_id=${langId}`,
  );
}

export function getComic(
  comicSlug: string,
  navigationContext?: ReaderNavigationContext,
) {
  return apiClient.get<ServerSingleResponse<Comic>>(
    `/v1/read/${comicSlug}`,
    {
      params: navigationContext
        ? buildContextQueryParams(navigationContext)
        : undefined,
    },
  );
}

export function getArtist(
  artistSlug: string,
  navigationContext?: ReaderNavigationContext,
) {
  return apiClient.get<ServerSingleResponse<Artist>>(
    `/v1/artists/${encodeURIComponent(artistSlug)}`,
    {
      params: navigationContext
        ? buildContextQueryParams(navigationContext)
        : undefined,
    },
  );
}

export function getUserProfile(
  username: string,
  navigationContext?: ReaderNavigationContext,
) {
  return apiClient.get<ServerSingleResponse<User>>(
    `/v1/user-profiles/${username}`,
    {
      params: navigationContext
        ? buildContextQueryParams(navigationContext)
        : undefined,
    },
  );
}

export function getBundleDetail(bundleUuid: string) {
  return apiClient.get<ServerSingleResponse<Bundle>>(
    `/v1/bundles/${bundleUuid}`,
  );
}

export interface UseBundlePurchaseFunnelOptions {
  /**
   * Whether to use external modal (setFunnelModalConfig) or slide-in funnel (setFunnelConfig)
   * - true: External modal (for cards, external contexts)
   * - false: Slide-in funnel (for bundle details within slide-in menu)
   */
  useExternalModal?: boolean;

  /**
   * Custom setFunnelConfig function (for slide-in menu contexts)
   * If not provided and useExternalModal is false, will throw error
   */
  setFunnelConfig?: (
    config?: import("../../funnel/types").FunnelConfigTypes,
  ) => void;

  /**
   * Enable debug logging
   */
  debug?: boolean;
}

export function unlockBundle(
  bundleUuid: string,
  payload: BundlePurchasePayload,
) {
  return apiClient.post<ServerSingleResponse<unknown>>(
    `/v1/bundles/${bundleUuid}/unlock`,
    { meta: {}, payload: payload },
  );
}
